import {
    type FC,
    useState,
    useEffect,
    useRef,
    type MouseEventHandler,
    type KeyboardEventHandler,
    type MouseEvent as ReactMouseEvent,
} from 'react';

import styles from './main.scss';

type Props = {
    currentPosition: number;
    onSlide: (arg0: number) => void;
    minRange?: number;
    maxRange?: number;
    showCurrentPosition?: boolean;
};

export const Slider: FC<Props> = ({
    currentPosition = 1,
    onSlide,
    minRange = 0.5,
    maxRange = 1.7,
    showCurrentPosition = false,
}) => {
    const [circleXOffset, setCircleXOffset] = useState(0);

    const lineRef = useRef<HTMLDivElement | null>(null);
    const delta = maxRange - minRange;
    const step = delta / 10;
    useEffect(() => {
        if (lineRef.current) {
            const rect = lineRef.current.getBoundingClientRect();
            const xOffset = ((currentPosition - minRange) / delta) * (rect.right - rect.left);
            setCircleXOffset(xOffset);
        }
    }, [currentPosition, minRange, delta]);

    function handleSlide(value: number): void {
        const boundValue = Math.max(minRange, Math.min(maxRange, value));
        onSlide(boundValue);
    }

    function handleCircleXOffset(value: number): void {
        if (!lineRef.current) {
            return;
        }
        const maxOffset = lineRef.current.offsetWidth;
        const boundValue = Math.max(0, Math.min(maxOffset, value));
        setCircleXOffset(boundValue);
    }

    function handleMouseMove(event: MouseEvent | ReactMouseEvent<HTMLDivElement>): void {
        if (!lineRef.current) {
            return;
        }

        const rect = lineRef.current.getBoundingClientRect();
        // The user's cursor position minus the left edge position of the line gives us the offset from the slider's line start
        const mouseXChange = event.clientX - rect.left;

        let xOffset;
        // If the user's cursor is greater than the start of the line, but also less than the end of the line,
        // the circle should be positioned at the change in x position, relative to the start of the line.
        // Otherwise, it the user's cursor is at the end/start of the line, the circle should be placed at the
        // start of the line's relative position (0), or at the end of the line's position relative to the start of
        // the line (position of line's right boundary - position of line's left boundary)
        if (event.clientX > rect.left && event.clientX < rect.right) {
            xOffset = mouseXChange;
        } else {
            xOffset = mouseXChange >= 0 ? rect.right - rect.left : 0;
        }

        // Convert position of circle to a value between 0 and 1 and round to nearest tenth
        const value = Math.round((xOffset / (rect.right - rect.left)) * 10) / 10;

        // Scale position such that it mimics the scaling of a browser defaulted input range slider and assign an aribtrary
        // y-intercept value that prevents objects from being scaled too small.
        const valProd = value * delta;

        const valueScaled = valProd + minRange;
        // Set slider value and position of circle relative to slider
        handleSlide(valueScaled);
        handleCircleXOffset(xOffset);
    }

    const handleKeyDown: KeyboardEventHandler = e => {
        if (!lineRef.current) {
            return;
        }
        const maxOffset = lineRef.current.offsetWidth;
        const xOffsetStep = maxOffset * step;
        let newPosition = currentPosition;
        let newOffset = circleXOffset;

        if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
            //up and right arrows
            newPosition = currentPosition + step;
            newOffset = circleXOffset + xOffsetStep;
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
            //down and left arrows
            newPosition = currentPosition - step;
            newOffset = circleXOffset - xOffsetStep;
        }
        // Set slider value and position of circle relative to slider
        handleSlide(newPosition);
        handleCircleXOffset(newOffset);
    };

    function handleMouseUp(): void {
        document.removeEventListener('mousemove', handleMouseMove);
    }

    const handleMouseDown: MouseEventHandler = e => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        e.preventDefault();
    };

    return (
        <div
            className={styles.line}
            ref={lineRef}
            role="slider"
            data-tn="slider-circle-wrapper"
            aria-valuemin={minRange}
            aria-valuemax={maxRange}
            aria-valuenow={currentPosition}
            tabIndex={0}
            onClick={handleMouseMove}
            onKeyDown={handleKeyDown}
        >
            {/* .line is handling all slider accessibility requirements */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
                data-tn="slider-circle"
                className={styles.circle}
                onMouseDown={handleMouseDown}
                style={{
                    transform: `translate3d(${circleXOffset}px, -50%, 0)`,
                }}
            >
                {showCurrentPosition && currentPosition}
            </div>
        </div>
    );
};
